import chalk from 'chalk';
import { ENTITY_KINDS, type ConfigDiff, type DiffBucket, type EntityKind } from './types.js';

const LABELS: Record<EntityKind, string> = {
  collections: 'collection',
  relationship_types: 'relationship-type',
  inbound_webhooks: 'inbound webhook',
  triggers: 'trigger',
  toolsets: 'toolset',
  tools: 'tool',
};

function bucket(diff: ConfigDiff, kind: EntityKind): DiffBucket<{ id: string }> | undefined {
  return diff[kind] as DiffBucket<{ id: string }> | undefined;
}

export function isDiffEmpty(diff: ConfigDiff): boolean {
  return ENTITY_KINDS.every((k) => {
    const b = bucket(diff, k);
    return !b || (b.added.length === 0 && b.modified.length === 0 && b.removed.length === 0);
  });
}

export interface Removal {
  kind: EntityKind;
  id: string;
}

/** Every entity present on the server but absent locally — candidates for `--prune`. */
export function collectRemovals(diff: ConfigDiff): Removal[] {
  const out: Removal[] = [];
  for (const kind of ENTITY_KINDS) {
    for (const e of bucket(diff, kind)?.removed ?? []) out.push({ kind, id: e.id });
  }
  return out;
}

export function countChanges(diff: ConfigDiff): { added: number; modified: number; removed: number } {
  let added = 0;
  let modified = 0;
  let removed = 0;
  for (const kind of ENTITY_KINDS) {
    const b = bucket(diff, kind);
    if (!b) continue;
    added += b.added.length;
    modified += b.modified.length;
    removed += b.removed.length;
  }
  return { added, modified, removed };
}

/**
 * Render a diff with colour: green `+` added, yellow `~` modified, red `-` removed. Removals are
 * the desired-state foot-gun, so they print a prominent note that `--prune` is required to apply.
 */
export function renderDiff(diff: ConfigDiff): string {
  const lines: string[] = [];
  for (const kind of ENTITY_KINDS) {
    const b = bucket(diff, kind);
    if (!b || (b.added.length === 0 && b.modified.length === 0 && b.removed.length === 0)) continue;
    lines.push(chalk.bold(LABELS[kind] + 's'));
    for (const e of b.added) lines.push(chalk.green(`  + ${e.id}`));
    for (const m of b.modified) lines.push(chalk.yellow(`  ~ ${m.after.id}`));
    for (const e of b.removed) lines.push(chalk.red(`  - ${e.id}`));
  }
  const { added, modified, removed } = countChanges(diff);
  lines.push('');
  lines.push(`${chalk.green(`${added} added`)}, ${chalk.yellow(`${modified} modified`)}, ${chalk.red(`${removed} removed`)}`);
  if (removed > 0) {
    lines.push(chalk.red(`  ${removed} entit${removed === 1 ? 'y' : 'ies'} exist on the server but not in your files — run with --prune to delete (cascades documents for collections).`));
  }
  return lines.join('\n');
}
